import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {
  DirectusService,
  DirectusTranslation,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
} from '@frontend/directus';
import {TenderSubcriteriaAttributeView} from '@frontend/shared-api';
import {TabStatusEnum} from './tab-status.model';
import {
  AttributeValueChange,
  DirectusDataMaps,
  ECTab,
  ModuleTabForQuestionnaire,
  SubcriteriaAttribute,
  SubcriteriaRenderer,
  SubcriteriaTab,
  TabBuilderOptions,
} from '../components/provider-questionnaire/provider-questionnaire.types';
import {isSC14} from '../constants/evaluation-criteria.constants';
import {PersistedAttributeValue} from '../components/provider-questionnaire/suggestion-review/template-suggestion.types';

@Injectable({
  providedIn: 'root',
})
export class TabManagementService {
  private readonly directusService = inject(DirectusService);

  buildSubcriteriaTab(params: {
    id: number;
    subcriteriaInfo: EvaluationSubCriteriaDirectus;
    attributes: SubcriteriaAttribute[];
    options?: TabBuilderOptions;
  }): SubcriteriaTab {
    const {id, subcriteriaInfo, attributes, options} = params;

    return {
      id,
      scNumber: subcriteriaInfo.sc_number,
      directusId: subcriteriaInfo.id,
      translations: subcriteriaInfo.translations || [],
      attributes,
      renderer: isSC14(subcriteriaInfo.sc_number)
        ? SubcriteriaRenderer.PROJECT_REFERENCES
        : SubcriteriaRenderer.ATTRIBUTES,
      isSaving: options?.isSaving ?? false,
    };
  }

  createECTab(
    ecId: string,
    ecAttributes: TenderSubcriteriaAttributeView[],
    maps: DirectusDataMaps,
    evaluationCriteriaMap: Map<string, EvaluationCriteria>,
    groupTenderAttributesBySubcriteriaFn: (
      attrs: TenderSubcriteriaAttributeView[],
    ) => Map<number, TenderSubcriteriaAttributeView[]>,
    createTabFromTenderAttributesFn: (
      subcriteriaId: number,
      subAttributes: TenderSubcriteriaAttributeView[],
      maps: DirectusDataMaps,
    ) => SubcriteriaTab | null,
  ): ECTab | null {
    if (ecAttributes.length === 0) return null;

    const groupedBySubcriteria = groupTenderAttributesBySubcriteriaFn(ecAttributes);
    const subcriteriaTabs: SubcriteriaTab[] = [];

    for (const [subcriteriaId, subAttributes] of groupedBySubcriteria.entries()) {
      const subcriteriaTab = createTabFromTenderAttributesFn(subcriteriaId, subAttributes, maps);

      if (subcriteriaTab) {
        subcriteriaTabs.push(subcriteriaTab);
      }
    }

    if (subcriteriaTabs.length === 0) return null;

    const evaluationCriteriaInfo = evaluationCriteriaMap.get(ecId);
    if (!evaluationCriteriaInfo) return null;

    return {
      id: evaluationCriteriaInfo.ecNumber,
      ecNumber: evaluationCriteriaInfo.ecNumber,
      title: evaluationCriteriaInfo.name,
      directusId: ecId,
      translations: evaluationCriteriaInfo.translations,
      subcriteriaTabs: subcriteriaTabs.toSorted((a, b) => a.scNumber - b.scNumber),
      isSaving: false,
    };
  }

  async groupSubcriteriaByModulesForEC3(
    subcriteriaTabs: SubcriteriaTab[],
    maps: DirectusDataMaps,
    supportedModuleFunctionIds?: number[],
  ): Promise<ModuleTabForQuestionnaire[]> {
    const moduleFunctionsMap = await firstValueFrom(
      this.directusService.loadModuleFunctionsGroupedByModule(),
    );
    const supportedFunctionIdsSet = supportedModuleFunctionIds
      ? new Set(supportedModuleFunctionIds)
      : undefined;

    const moduleGroups = new Map<
      number,
      {
        moduleId: number;
        moduleName: string;
        moduleTranslations: DirectusTranslation[];
        subcriteriaTabs: SubcriteriaTab[];
      }
    >();

    for (const subTab of subcriteriaTabs) {
      const moduleInfo = this.findModuleForSubcriteria(
        subTab,
        moduleFunctionsMap,
        maps,
        supportedFunctionIdsSet,
      );
      if (moduleInfo) {
        if (!moduleGroups.has(moduleInfo.moduleId)) {
          moduleGroups.set(moduleInfo.moduleId, {
            moduleId: moduleInfo.moduleId,
            moduleName: moduleInfo.moduleName,
            moduleTranslations: moduleInfo.moduleTranslations,
            subcriteriaTabs: [],
          });
        }
        moduleGroups.get(moduleInfo.moduleId)?.subcriteriaTabs.push(subTab);
      }
    }

    return Array.from(moduleGroups.values())
      .map((group) => ({
        ...group,
        status: TabStatusEnum.NOT_STARTED,
        loaded: false,
        subcriteriaTabs: group.subcriteriaTabs.toSorted((a, b) => a.scNumber - b.scNumber),
      }))
      .toSorted((a, b) => a.moduleName.localeCompare(b.moduleName));
  }

  findModuleForSubcriteria(
    subcriteriaTab: SubcriteriaTab,
    moduleFunctionsMap: Map<number, any>,
    maps: DirectusDataMaps,
    supportedFunctionIds?: Set<number>,
  ): {moduleId: number; moduleName: string; moduleTranslations: DirectusTranslation[]} | null {
    const subcriteriaInfo = maps.subcriteriaMap.get(subcriteriaTab.directusId);
    if (!subcriteriaInfo?.modul_function) return null;

    const functionId = subcriteriaInfo.modul_function;
    if (supportedFunctionIds && !supportedFunctionIds.has(functionId)) return null;

    for (const [moduleId, moduleData] of moduleFunctionsMap) {
      const module = moduleData as {
        name: string;
        translations?: DirectusTranslation[];
        functions?: {id: number; name: string}[];
      };

      const hasFunction = module.functions?.some((f) => f.id === functionId);
      if (hasFunction) {
        return {
          moduleId,
          moduleName: module.name,
          moduleTranslations: module.translations || [],
        };
      }
    }

    return null;
  }

  updateAttributeInSubcriteriaTab<T extends SubcriteriaTab>(
    change: AttributeValueChange,
    tabs: T[],
    currentTabId: number,
  ): T[] {
    const currentTab = tabs.find((tab) => tab.id === currentTabId);
    if (!currentTab) return tabs;

    const updatedAttributes = currentTab.attributes.map((attr) =>
      attr.directusId === change.directusId ? {...attr, ...change, hasChanges: true} : attr,
    );

    return tabs.map((tab) =>
      tab.id === currentTabId ? {...tab, attributes: updatedAttributes} : tab,
    );
  }

  handleStandardECAttributeChange<T extends ECTab>(
    change: AttributeValueChange,
    ecTabs: T[],
    ecTabId: number,
    subTabId: number,
  ): T[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId) return ecTab;

      const updatedSubcriteriaTabs = this.updateAttributeInSubcriteriaTab(
        change,
        ecTab.subcriteriaTabs,
        subTabId,
      );

      return {
        ...ecTab,
        subcriteriaTabs: updatedSubcriteriaTabs,
      };
    });
  }

  setSubcriteriaTabSavingState<T extends SubcriteriaTab>(
    tabs: T[],
    tabId: number,
    isSaving: boolean,
  ): T[] {
    return tabs.map((tab) => (tab.id === tabId ? {...tab, isSaving} : tab));
  }

  setStandardECSavingState<T extends ECTab>(
    ecTabs: T[],
    ecTabId: number,
    subTabId: number,
    isSaving: boolean,
  ): T[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId) return ecTab;

      const updatedSubcriteriaTabs = this.setSubcriteriaTabSavingState(
        ecTab.subcriteriaTabs,
        subTabId,
        isSaving,
      );

      return {
        ...ecTab,
        subcriteriaTabs: updatedSubcriteriaTabs,
      };
    });
  }

  resetSubcriteriaTabChanges<T extends SubcriteriaTab>(tabs: T[], tabId: number): T[] {
    return tabs.map((tab) => {
      if (tab.id !== tabId) return tab;

      const updatedAttributes = tab.attributes.map((attr) => ({
        ...attr,
        hasChanges: false,
      }));

      return {
        ...tab,
        attributes: updatedAttributes,
      };
    });
  }

  resetStandardECChanges<T extends ECTab>(ecTabs: T[], ecTabId: number, subTabId: number): T[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId) return ecTab;

      const updatedSubcriteriaTabs = this.resetSubcriteriaTabChanges(
        ecTab.subcriteriaTabs,
        subTabId,
      );

      return {
        ...ecTab,
        subcriteriaTabs: updatedSubcriteriaTabs,
      };
    });
  }

  getCurrentStandardSubTab(
    ecTabs: ECTab[],
    activeECIndex: number,
    activeSubIndex: number,
  ): SubcriteriaTab | undefined {
    const currentECTab = ecTabs[activeECIndex];
    if (!currentECTab) return undefined;

    return currentECTab.subcriteriaTabs[activeSubIndex];
  }

  getCurrentEC3SubTab(
    ecTabs: ECTab[],
    activeECIndex: number,
    activeModuleIndex: number,
    activeSubIndex: number,
  ): SubcriteriaTab | undefined {
    const currentECTab = ecTabs[activeECIndex];
    if (!currentECTab?.moduleTabs) return undefined;

    const currentModuleTab = currentECTab.moduleTabs[activeModuleIndex];
    if (!currentModuleTab) return undefined;

    return currentModuleTab.subcriteriaTabs[activeSubIndex];
  }

  getCurrentEC3ModuleTab(
    ecTabs: ECTab[],
    activeECIndex: number,
    activeModuleIndex: number,
  ): ModuleTabForQuestionnaire | undefined {
    const currentECTab = ecTabs[activeECIndex];
    if (!currentECTab?.moduleTabs) return undefined;

    return currentECTab.moduleTabs[activeModuleIndex];
  }

  handleEC3AttributeChange(
    change: AttributeValueChange,
    ecTabs: ECTab[],
    ecTabId: number,
    moduleId: number,
    subTabId: number,
  ): ECTab[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId || !ecTab.moduleTabs) return ecTab;

      const updatedModuleTabs = ecTab.moduleTabs.map((moduleTab) => {
        if (moduleTab.moduleId !== moduleId) return moduleTab;

        const updatedSubcriteriaTabs = this.updateAttributeInSubcriteriaTab(
          change,
          moduleTab.subcriteriaTabs,
          subTabId,
        );

        return {...moduleTab, subcriteriaTabs: updatedSubcriteriaTabs};
      });

      return {...ecTab, moduleTabs: updatedModuleTabs};
    });
  }

  setEC3SavingState<T extends ECTab>(
    ecTabs: T[],
    ecTabId: number,
    moduleId: number,
    subTabId: number,
    isSaving: boolean,
  ): T[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId || !ecTab.moduleTabs) return ecTab;

      const updatedModuleTabs = ecTab.moduleTabs.map((moduleTab) => {
        if (moduleTab.moduleId !== moduleId) return moduleTab;

        const updatedSubcriteriaTabs = this.setSubcriteriaTabSavingState(
          moduleTab.subcriteriaTabs,
          subTabId,
          isSaving,
        );

        return {...moduleTab, subcriteriaTabs: updatedSubcriteriaTabs};
      });

      return {...ecTab, moduleTabs: updatedModuleTabs};
    });
  }

  resetEC3Changes<T extends ECTab>(
    ecTabs: T[],
    ecTabId: number,
    moduleId: number,
    subTabId: number,
  ): T[] {
    return ecTabs.map((ecTab) => {
      if (ecTab.id !== ecTabId || !ecTab.moduleTabs) return ecTab;

      const updatedModuleTabs = ecTab.moduleTabs.map((moduleTab) => {
        if (moduleTab.moduleId !== moduleId) return moduleTab;

        const updatedSubcriteriaTabs = this.resetSubcriteriaTabChanges(
          moduleTab.subcriteriaTabs,
          subTabId,
        );

        return {...moduleTab, subcriteriaTabs: updatedSubcriteriaTabs};
      });

      return {...ecTab, moduleTabs: updatedModuleTabs};
    });
  }

  applyPersistedAttributeValue(ecTabs: ECTab[], value: PersistedAttributeValue): ECTab[] {
    return ecTabs.map((ecTab) => ({
      ...ecTab,
      subcriteriaTabs: this.applyPersistedValueToTabs(ecTab.subcriteriaTabs, value),
      moduleTabs: ecTab.moduleTabs?.map((moduleTab) => ({
        ...moduleTab,
        subcriteriaTabs: this.applyPersistedValueToTabs(moduleTab.subcriteriaTabs, value),
      })),
    }));
  }

  private applyPersistedValueToTabs(
    tabs: SubcriteriaTab[],
    value: PersistedAttributeValue,
  ): SubcriteriaTab[] {
    return tabs.map((tab) => ({
      ...tab,
      attributes: tab.attributes.map((attr) =>
        attr.directusId === value.directusId
          ? {
              ...attr,
              score: value.score,
              scoreAbsent: false,
              capabilityLevel: value.capabilityLevel,
              provisioningType: value.provisioningType,
              singleStage: value.capabilityLevel === undefined,
              hasChanges: false,
            }
          : attr,
      ),
    }));
  }
}
